import React, { useState, useEffect } from "react";
import { useForm, Controller, useFieldArray } from "react-hook-form";
import CustomDatatableGrid from "../../components/CustomDatatableGrid";
import { get, post } from "../../services/api";
import Select from "react-select";
import { toast } from "react-toastify";
import { customTableStyles } from "../../assets/CustomTableStyles";
import * as XLSX from "xlsx";
import jsPDF from "jspdf";
import "jspdf-autotable";
import logo from "../../assets/img/logo-full.png";

function LoanedBooksList() {
  const token = localStorage.getItem("token");
  const organizationId = localStorage.getItem("organizationId");
  const savedCenterId =
    localStorage.getItem("centerId") === "null"
      ? null
      : localStorage.getItem("centerId");

  const [triggerRerender, setTriggerRerender] = useState(false);

  const {
    register,
    formState: { errors, isSubmitting },
    control,
    handleSubmit,
    watch,
    setValue,
    getValues,
    trigger,
    reset,
    setError,
  } = useForm({
    defaultValues: {},
  });

  const [centerId, setCenterId] = useState(savedCenterId || null);
  const [libraryId, setLibraryId] = useState("");
  const [centersData, setCentersData] = useState([]);
  const [librariesData, setLibrariesData] = useState([]);
  const [loanedBooksList, setLoanedBooksList] = useState([]);
  const [filteredLoanedBooksList, setFilteredLoanedBooksList] = useState([]); // For search

  const [searchTerm, setSearchTerm] = useState("");

  useEffect(() => {
    const fetchCenters = async () => {
      try {
        const response = await get(
          `Dropdown/GetAllCentersByOrganizationId?organizationId=${organizationId}`,
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          }
        );

        setCentersData(response);

        if (savedCenterId) {
          const selectedCenter = response.find(
            (center) => center.id === parseInt(savedCenterId, 10)
          );
          if (selectedCenter) {
            setValue("centerId", {
              value: selectedCenter.id,
              label: selectedCenter.name,
            });
          }
        }
      } catch (error) {
        console.error("Error fetching countries:", error);
      }
    };

    fetchCenters();
  }, [organizationId]);

  const getLibrariesListByCenterId = async (centerId) => {
    if (!token) {
      console.error("Unauthorized.");
      return;
    }

    try {
      const response = await get(
        `LMS/GetLibraryByCenterIdAsync?centerId=${centerId}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );
      setLibrariesData(response?.libraryService);
    } catch (error) {
      console.error("Error fetching libraries:", error);
    }
  };

  useEffect(() => {
    if (centerId) {
      getLibrariesListByCenterId(centerId);
    }
  }, [centerId]);

  const fetchLoanedBooks = async () => {
    try {
      const response = await get(
        `LMS/GetBooksBylibraryIdAsync?libraryId=${libraryId}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      setLoanedBooksList(response?.bookService || []);
    } catch (error) {
      console.error("Error fetching loaned books:", error);
    }
  };

  useEffect(() => {
    if (libraryId) {
      fetchLoanedBooks();
    }
  }, [libraryId]);
  /* eslint-disable react-hooks/exhaustive-deps */
  const columns = React.useMemo(
    () => [
      {
        accessorKey: "mrt-row-numbers",
        header: "Sr No.",
        enableColumnOrdering: false,
        enableEditing: false, 
        enableSorting: false,
        size: 100,
        Cell: ({ row }) => row.index + 1,
      },
      {
        accessorKey: "title",
        header: "Book",
        Cell: ({ row }) => {
            let imageSrc = "";
    
            if (!row.original.image) {
              imageSrc = `${process.env.PUBLIC_URL}/image/team/avatar-placeholder.jpg`;
            } else {
              imageSrc = `${process.env.REACT_APP_LIVE_DOMAIN_URL}ExternalImages/${row.original.imagePath}`;
            }
    
            return (
              <div className="d-flex align-items-center gap-2">
                <div className="table-listing-image-preview">
                  <img src={imageSrc} alt="Image" />
                </div>
                <div className="">{row.original.title}</div>
              </div>
            );
          },
      },
      {
        accessorKey: "isbn",
        header: "ISBN",
      },
      {
        accessorKey: "genre",
        header: "Genre",
      },
      {
        accessorKey: "publishedYear",
        header: "Published Year",
      },
      {
        accessorKey: "copiesAvailable",
        header: "No. of Copies",
      },
      {
        accessorKey: "stockQty",
        header: "Available Stock",
      },
      {
        accessorKey: "status",
        header: "Status",
        Cell: ({ row }) => (
            <div>
              {row.original.stockQty > 0 ? (
                <span className="badge bg-success fs-10">In Stock</span>
              ) : (
                <span className="badge bg-danger fs-10">Out of Stock</span>
              )}
            </div>
          ),
      },
    ],
    []
  );

  const exportToExcel = () => {
    const dataToExport = filteredLoanedBooksList.map((row, index) => ({
      "Sr No": index + 1,
      Book: row.title,
      ISBN: row.isbn,
      Genre: row.genre,
      "Published Year": row.publishedYear,
      "No. of Copies": row.copiesAvailable,
      "Available Stock": row.stockQty,
      Status: row.stockQty > 0 ? "In Stock" : "Out of Stock",
    }));
    const worksheet = XLSX.utils.json_to_sheet(dataToExport);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Loaned Books");
    XLSX.writeFile(workbook, "LoanedBooks.xlsx");
  };

  const loadImage = (url) => {
    return new Promise((resolve, reject) => {
      const img = new Image();
      img.crossOrigin = "Anonymous";
      img.src = url;
      img.onload = () => resolve(img);
      img.onerror = (e) => reject(e);
    });
  };

  const exportToPDF = async () => {
    const doc = new jsPDF();
    const pageWidth = doc.internal.pageSize.getWidth();
    const logoWidth = 30; // Adjust logo width as needed
    const logoHeight = 8; // Adjust logo height as needed
    const logoX = (pageWidth - logoWidth) / 2;
    const logoY = 10;
    
    const roleIdLoggedIn = localStorage.getItem("roleId");
    const orgLogoPath = localStorage.getItem("organizationLogoPath");
    const centerLogoPath = localStorage.getItem("centerLogoPath");
    const orgLogo = `${process.env.REACT_APP_LIVE_DOMAIN_URL}ExternalImages${orgLogoPath}`;
    const centerLogo = `${process.env.REACT_APP_LIVE_DOMAIN_URL}ExternalImages${centerLogoPath}`;

    let logoToUse = logo; // Default imported logo
    const sidebarLogoUrl =
      roleIdLoggedIn == "2" ? orgLogo : roleIdLoggedIn == "3" ? centerLogo : null;

    if (sidebarLogoUrl) {
      try {
        logoToUse = await loadImage(sidebarLogoUrl);
      } catch (error) {
        console.warn(
          "Failed to load sidebar logo, falling back to default.",
          error
        );
      }
    }

    // doc.addImage(logoToUse, "PNG", logoX, logoY, logoWidth, logoHeight);

    const title = "Loaned Books List";
    const titleFontSize = 12;
    doc.setFontSize(titleFontSize);
    doc.setFont("helvetica", "bold");
    const textWidth = doc.getTextWidth(title);
    const titleX = (pageWidth - textWidth) / 2;
    const titleY = logoY + logoHeight + 10;
    doc.text(title, titleX, titleY);

    const includedColumns = columns.filter(
      (column) => column.accessorKey !== "actions" && column.accessorKey !== "mrt-row-numbers"
    );

    const headers = ["Sr No.", ...includedColumns.map((col) => col.header)];

    const bodyData = filteredLoanedBooksList.map((row, index) => {
        return [
          index + 1,
          row.title,
          row.isbn,
          row.genre,
          row.publishedYear,
          row.copiesAvailable,
          row.stockQty,
          row.stockQty > 0 ? "In Stock" : "Out of Stock",
        ];
      });

    doc.autoTable({
      head: [headers],
      body: bodyData,
      startY: titleY + 10,
      theme: "striped",
      headStyles: {
        fillColor: [132, 23, 26],
        textColor: [255, 255, 255],
      },
    });

    doc.save("LoanedBooksList.pdf");
  };

  useEffect(() => {
    const tooltipTriggerList = Array.from(
      document.querySelectorAll('[data-bs-toggle="tooltip"]')
    );
    tooltipTriggerList.forEach((tooltipTriggerEl) => {
      new window.bootstrap.Tooltip(tooltipTriggerEl);
    });
  }, []);

  useEffect(() => {
    const filtered = loanedBooksList.filter((book) => {
      const searchLower = searchTerm.toLowerCase();
      return (
        book.title.toLowerCase().includes(searchLower) ||
        book.isbn.toLowerCase().includes(searchLower) ||
        book.genre.toLowerCase().includes(searchLower) ||
        book.publishedYear.toString().includes(searchLower) ||
        book.copiesAvailable.toString().includes(searchLower) ||
        book.stockQty.toString().includes(searchLower) ||
        (book.stockQty > 0 ? "In Stock" : "Out of Stock")
          .toLowerCase()
          .includes(searchLower)
      );
    });
    setFilteredLoanedBooksList(filtered);
  }, [searchTerm, loanedBooksList]);

  return (
    <>
      <div className="page-header d-flex mb-2">
        <h2 className="page-heading mb-2 fs-7">Loaned Books</h2>
      </div>

      <div className="row gx-2 mb-3">
        <div className="col-md-2">
          <label className="form-label">Center</label>
          <Controller
            name={`centerId`}
            control={control}
            rules={{ required: "This field is required." }}
            render={({ field }) => (
              <Select
                {...field}
                options={centersData.map((center) => ({
                  value: center.id,
                  label: center.name,
                }))}
                isDisabled={savedCenterId !== "0"}
                onChange={(selectedOption, actionMeta) => {
                  if (actionMeta.action === "clear") {
                    setValue(`centerId`, null);
                    setCenterId(null);
                  } else {
                    setValue(
                      `centerId`,
                      selectedOption || { value: 0, label: "" }
                    );
                    setCenterId(selectedOption.value);
                  }
                  setValue("libraryId", null);
                  setLibraryId("0");
                  setLibrariesData([]);
                  trigger(`centerId`);
                }}
                className="react-select-style"
                classNamePrefix="react-select-style-pref"
                isClearable
              />
            )}
          />
        </div>
        <div className="col-md-2">
          <label className="form-label">Library</label>
          <Controller
            name={`libraryId`}
            control={control}
            rules={{ required: "This field is required." }}
            render={({ field }) => (
              <Select
                {...field}
                options={librariesData.map((library) => ({
                  value: library.id,
                  label: library.name,
                }))}
                onChange={(selectedOption, actionMeta) => {
                  if (actionMeta.action === "clear") {
                    setValue(`libraryId`, null);
                    setLibraryId("0");
                  } else {
                    setValue(
                      `libraryId`,
                      selectedOption || { value: 0, label: "" }
                    );
                    setLibraryId(selectedOption.value);
                  }
                  trigger(`libraryId`);
                }}
                className="react-select-style"
                classNamePrefix="react-select-style-pref"
                isClearable
              />
            )}
          />
        </div>
      </div>

      <div className="card">
        <div className="card-body">
          <CustomDatatableGrid
            columns={columns}
            data={filteredLoanedBooksList}
            onExportExcel={exportToExcel}
            onExportPDF={exportToPDF}
          />
        </div>
      </div>
    </>
  );
}

export default LoanedBooksList;
